import { useCallback, useEffect, useState } from 'react';
import {
  ClipboardCheck, Plus, X, Play, Check, ShieldCheck, Trash2,
} from 'lucide-react';
import whsService from '../../../services/whs';
import type {
  WhsAction, ActionStatus, ActionType, ActionPriority, HierarchyControl,
} from '../../../types/whs';
import {
  Badge, ACTION_STATUS_LABEL, ACTION_STATUS_CLASS, PRIORITY_LABEL, PRIORITY_CLASS,
  ACTION_TYPE_LABEL, HIERARCHY_LABEL,
} from '../../../components/whs/whsConstants';
import WhsGate from '../../../components/whs/WhsGate';
import PageHeader from '../../../components/PageHeader';
import Button from '../../../components/ui/Button';
import userService from '../../../services/user';
import type { UserData } from '../../../services/user';
import { useOrgTimezone, formatLocalDateInTz, getTodayInTz } from '../../../utils/timezone';

const inputClass =
  'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

const ACTION_STATUS_KEYS = Object.keys(ACTION_STATUS_LABEL) as ActionStatus[];
const ACTION_TYPE_KEYS = Object.keys(ACTION_TYPE_LABEL) as ActionType[];
const PRIORITY_KEYS = Object.keys(PRIORITY_LABEL) as ActionPriority[];
const HIERARCHY_KEYS = Object.keys(HIERARCHY_LABEL) as HierarchyControl[];

function userLabel(u: UserData): string {
  const name = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim();
  return name || u.username;
}

interface NewForm {
  title: string;
  description: string;
  action_type: ActionType;
  priority: ActionPriority;
  hierarchy_control: HierarchyControl | '';
  assigned_to: number | null;
  due_date: string;
}

const EMPTY_FORM: NewForm = {
  title: '',
  description: '',
  action_type: 'corrective',
  priority: 'medium',
  hierarchy_control: '',
  assigned_to: null,
  due_date: '',
};

function ActionsInner() {
  const orgTz = useOrgTimezone();
  const today = getTodayInTz(orgTz);

  const [actions, setActions] = useState<WhsAction[]>([]);
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<ActionStatus | ''>('');
  const [assigneeFilter, setAssigneeFilter] = useState<number | ''>('');
  const [overdueOnly, setOverdueOnly] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<NewForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await whsService.listActions({
      status: statusFilter || undefined,
      assigned_to: assigneeFilter || undefined,
      overdue: overdueOnly || undefined,
    });
    if (res.success) setActions(res.data);
    else setError(res.error.message);
    setLoading(false);
  }, [statusFilter, assigneeFilter, overdueOnly]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let active = true;
    userService.getUsers().then((res) => {
      if (active && res.success) setUsers(res.data);
    });
    return () => { active = false; };
  }, []);

  const isOverdue = (a: WhsAction): boolean =>
    !!a.due_date && a.due_date < today && (a.status === 'open' || a.status === 'in_progress');

  const counts = {
    open: actions.filter((a) => a.status === 'open').length,
    overdue: actions.filter(isOverdue).length,
    completed: actions.filter((a) => a.status === 'completed').length,
    verified: actions.filter((a) => a.status === 'verified').length,
  };

  const chips = [
    { label: 'Open', value: counts.open, cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    { label: 'Overdue', value: counts.overdue, cls: 'bg-red-50 text-red-700 border-red-200' },
    { label: 'Completed', value: counts.completed, cls: 'bg-teal-50 text-teal-700 border-teal-200' },
    { label: 'Verified', value: counts.verified, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  ];

  const patchAction = async (id: number, data: Partial<WhsAction>) => {
    const res = await whsService.updateAction(id, data);
    if (res.success) await load();
    else setError(res.error.message);
  };

  const markCompleted = async (a: WhsAction) => {
    const note = window.prompt('Completion note (optional):', a.completion_note ?? '');
    if (note === null) return;
    await patchAction(a.id, { status: 'completed', completion_note: note || null });
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this action?')) return;
    const res = await whsService.deleteAction(id);
    if (res.success) await load();
    else setError(res.error.message);
  };

  const openModal = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setModalOpen(true);
  };

  const handleCreate = async () => {
    if (!form.title.trim()) {
      setFormError('A title is required.');
      return;
    }
    setSaving(true);
    setFormError(null);
    const res = await whsService.createAction({
      source_type: 'manual',
      title: form.title.trim(),
      description: form.description.trim() || null,
      action_type: form.action_type,
      priority: form.priority,
      hierarchy_control: form.hierarchy_control || null,
      assigned_to: form.assigned_to,
      due_date: form.due_date || null,
    });
    setSaving(false);
    if (res.success) {
      setModalOpen(false);
      await load();
    } else {
      setFormError(res.error.message);
    }
  };

  const assigneeName = (a: WhsAction): string =>
    `${a.assignee_first_name ?? ''} ${a.assignee_last_name ?? ''}`.trim() || '—';

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Corrective Actions (CAPA)"
        subtitle="Track corrective & preventive actions to completion and verification"
        icon={<ClipboardCheck className="h-6 w-6 text-white" />}
        actionButtons={
          <Button size="sm" onClick={openModal}>
            <Plus className="h-4 w-4" /> New action
          </Button>
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
        )}

        {/* Summary chips */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {chips.map((c) => (
            <div key={c.label} className={`rounded-xl border p-4 ${c.cls}`}>
              <p className="text-xs font-medium uppercase tracking-wide">{c.label}</p>
              <p className="mt-1 text-2xl font-bold">{c.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ActionStatus | '')}
            className={`${inputClass} max-w-[200px]`}
          >
            <option value="">All statuses</option>
            {ACTION_STATUS_KEYS.map((s) => (
              <option key={s} value={s}>{ACTION_STATUS_LABEL[s]}</option>
            ))}
          </select>
          <select
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value ? Number(e.target.value) : '')}
            className={`${inputClass} max-w-[240px]`}
          >
            <option value="">All assignees</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{userLabel(u)}</option>
            ))}
          </select>
          <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
            <input
              type="checkbox"
              checked={overdueOnly}
              onChange={(e) => setOverdueOnly(e.target.checked)}
              className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
            />
            Overdue only
          </label>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
          {loading ? (
            <p className="p-6 text-sm text-neutral-400">Loading…</p>
          ) : actions.length === 0 ? (
            <p className="p-6 text-sm text-neutral-400">No actions found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr className="text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                    <th className="px-4 py-3">Action</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Priority</th>
                    <th className="px-4 py-3">Control</th>
                    <th className="px-4 py-3">Assignee</th>
                    <th className="px-4 py-3">Due</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 text-sm">
                  {actions.map((a) => (
                    <tr key={a.id} className="align-top">
                      <td className="px-4 py-3 max-w-xs">
                        <div className="font-medium text-neutral-900">{a.title}</div>
                        <div className="text-xs text-neutral-400 mt-0.5 capitalize">{a.source_type.replace(/_/g, ' ')}</div>
                        {a.description && (
                          <div className="text-xs text-neutral-500 mt-1 whitespace-pre-wrap">{a.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-neutral-600">{ACTION_TYPE_LABEL[a.action_type]}</td>
                      <td className="px-4 py-3">
                        <Badge label={PRIORITY_LABEL[a.priority]} className={PRIORITY_CLASS[a.priority]} />
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-600">
                        {a.hierarchy_control ? HIERARCHY_LABEL[a.hierarchy_control] : '—'}
                      </td>
                      <td className="px-4 py-3 text-neutral-600">{assigneeName(a)}</td>
                      <td className="px-4 py-3">
                        {a.due_date ? (
                          <span className={isOverdue(a) ? 'text-red-600 font-semibold' : 'text-neutral-600'}>
                            {formatLocalDateInTz(a.due_date, orgTz)}
                          </span>
                        ) : (
                          <span className="text-neutral-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge label={ACTION_STATUS_LABEL[a.status]} className={ACTION_STATUS_CLASS[a.status]} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {a.status === 'open' && (
                            <button
                              onClick={() => patchAction(a.id, { status: 'in_progress' })}
                              className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-50"
                              title="Mark in progress"
                              aria-label="Mark in progress"
                            >
                              <Play className="h-4 w-4" />
                            </button>
                          )}
                          {(a.status === 'open' || a.status === 'in_progress') && (
                            <button
                              onClick={() => markCompleted(a)}
                              className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50"
                              title="Mark completed"
                              aria-label="Mark completed"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                          )}
                          {a.status === 'completed' && (
                            <button
                              onClick={() => patchAction(a.id, { status: 'verified' })}
                              className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50"
                              title="Mark verified"
                              aria-label="Mark verified"
                            >
                              <ShieldCheck className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(a.id)}
                            className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"
                            title="Delete"
                            aria-label="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* New action modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-neutral-900">New action</h2>
              <button onClick={() => setModalOpen(false)} className="text-neutral-400 hover:text-neutral-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              {formError && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{formError}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Title <span className="text-red-500">*</span></label>
                <input
                  className={inputClass}
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
                <textarea
                  className={inputClass}
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Type</label>
                  <select
                    className={inputClass}
                    value={form.action_type}
                    onChange={(e) => setForm({ ...form, action_type: e.target.value as ActionType })}
                  >
                    {ACTION_TYPE_KEYS.map((t) => (
                      <option key={t} value={t}>{ACTION_TYPE_LABEL[t]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Priority</label>
                  <select
                    className={inputClass}
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value as ActionPriority })}
                  >
                    {PRIORITY_KEYS.map((p) => (
                      <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Hierarchy of control</label>
                <select
                  className={inputClass}
                  value={form.hierarchy_control}
                  onChange={(e) => setForm({ ...form, hierarchy_control: e.target.value as HierarchyControl | '' })}
                >
                  <option value="">Select…</option>
                  {HIERARCHY_KEYS.map((h) => (
                    <option key={h} value={h}>{HIERARCHY_LABEL[h]}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Assigned to</label>
                  <select
                    className={inputClass}
                    value={form.assigned_to ?? ''}
                    onChange={(e) => setForm({ ...form, assigned_to: e.target.value ? Number(e.target.value) : null })}
                  >
                    <option value="">Unassigned</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{userLabel(u)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Due date</label>
                  <input
                    type="date"
                    className={inputClass}
                    value={form.due_date}
                    onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-neutral-200 px-5 py-4">
              <Button variant="secondary" size="sm" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={handleCreate} isLoading={saving}>Create</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WhsActions() {
  return <WhsGate><ActionsInner /></WhsGate>;
}
